import { useState } from "react";
import { Phone, CheckCircle2, XCircle, ChevronDown, ChevronRight } from "lucide-react";
import { getValidationStatusIcon } from "../../../utils/validationUtils.js";

export default function TrackingNumbersValidation({ validation }) {
    const [showFoundNumbers, setShowFoundNumbers] = useState(false);

    const getScoreColor = (score) => {
        if (score >= 90) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        if (score >= 75) return 'bg-blue-100 text-blue-700 border-blue-200';
        if (score >= 50) return 'bg-amber-100 text-amber-700 border-amber-200';
        return 'bg-red-100 text-red-700 border-red-200';
    };

    const expectedNumber = validation.expectedDID?.number || 'N/A';
    const pageType = validation.expectedDID?.pageType || 'Unknown';
    const foundCount = validation.foundDIDs?.length || 0;
    const correctFound = validation.correctDIDFound;

    return (
        <div className="border-l-4 border-l-cyan-500 border-t border-r border-b border-slate-200 rounded-lg overflow-hidden">
            {/* Compact Header */}
            <div className="bg-cyan-50/30 p-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Phone size={16} className="text-blue-600" />
                        <h5 className="font-medium text-slate-900">Tracking Numbers</h5>
                        {getValidationStatusIcon(validation.status, validation.score)}
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Status Badge */}
                        {correctFound ? (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                ✓ DID Found
                            </span>
                        ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                ✗ DID Missing
                            </span>
                        )}
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold border ${getScoreColor(validation.score)}`}>
                            {validation.score}%
                        </div>
                    </div>
                </div>
            </div>

            {/* Compact Info Grid */}
            <div className="p-3 bg-slate-50/50 grid grid-cols-2 gap-2 border-b border-slate-200">
                <div className="bg-white rounded border border-slate-200 p-2">
                    <div className="text-xs text-slate-500 mb-1">Expected DID</div>
                    <div className="font-mono text-sm font-semibold text-slate-900">{expectedNumber}</div>
                </div>
                <div className="bg-white rounded border border-slate-200 p-2">
                    <div className="text-xs text-slate-500 mb-1">Page Type</div>
                    <div className="text-sm font-medium text-slate-900">{pageType}</div>
                </div>
            </div>

            {/* Found Numbers Section - Collapsible */}
            {foundCount > 0 && (
                <div>
                    <button
                        onClick={() => setShowFoundNumbers(!showFoundNumbers)}
                        className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-100"
                    >
                        <div className="flex items-center gap-2">
                            {showFoundNumbers ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <Phone size={14} className="text-slate-600" />
                            <span className="text-sm font-medium text-slate-700">Found Numbers</span>
                            <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{foundCount}</span>
                        </div>
                    </button>
                    {showFoundNumbers && (
                        <div className="px-3 pb-3 pt-2 space-y-1.5 bg-slate-50/30">
                            {validation.foundDIDs.map((phone, i) => {
                                const isCorrect = phone === expectedNumber;
                                return (
                                    <div
                                        key={i}
                                        className={`flex items-center justify-between p-2 rounded border text-xs ${
                                            isCorrect
                                                ? 'bg-emerald-50 border-emerald-200'
                                                : 'bg-white border-slate-200'
                                        }`}
                                    >
                                        <span className={`font-mono font-medium ${
                                            isCorrect ? 'text-emerald-900' : 'text-slate-700'
                                        }`}>
                                            {phone}
                                        </span>
                                        {isCorrect && (
                                            <span className="flex items-center gap-1 text-emerald-700 font-medium">
                                                <CheckCircle2 size={12} />
                                                Correct
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* No Numbers Found State */}
            {foundCount === 0 && (
                <div className="p-3 text-center">
                    <div className="inline-flex items-center gap-2 text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">
                        <XCircle size={14} />
                        No phone numbers detected
                    </div>
                </div>
            )}
        </div>
    );
}
